/* External */
import PropTypes from 'prop-types';
import OAuth from 'oauth-1.0a';
import HmacSHA1 from 'crypto-js/hmac-sha1';
import Base64 from 'crypto-js/enc-base64';

/* Utils */
import { WidevinePsshData } from './widevine_pssh_data.proto';

/* Constants */
export const ISP_LICENSE_HEADER_NAME = 'X-ISP-TOKEN';
export const ISP_BASE_FAIRPLAY_CERT_URL = `${process.env.REACT_APP_DRM_FAIRPLAY_HOST}/api/AppCert`;
export const ISP_BASE_FAIRPLAY_LICENSE_URL = `${process.env.REACT_APP_DRM_FAIRPLAY_HOST}/api/license`;
export const ISP_PLAYREADY_LICENSE_URL = `${process.env.REACT_APP_DRM_PLAYREADY_HOST}/api/license`;
export const ISP_BASE_WIDEVINE_LICENSE_URL = `${process.env.REACT_APP_DRM_WIDEVINE_HOST}/widevine/api/license`;

/**
 * Extracts the PSSH initialization data for widevine key system from a DASH manifest
 * @param {string} manifest - The DASH manifest to extract de PSSH data from
 * @returns {Uint8Array} The PSSH init data for widevine key system
 */
function getWidevinePSSHArrayFromDashManifest(manifest) {
  const parser = new DOMParser();

  const parsedManifest = parser.parseFromString(manifest, 'text/xml');
  const psshElement = parsedManifest.querySelector(
    'ContentProtection[schemeIdUri="urn:uuid:edef8ba9-79d6-4ace-a3c8-27dcd51d21ed"] > pssh',
  );
  const encodedPSSH = psshElement.innerHTML || psshElement.textContent;
  return Uint8Array.from(atob(encodedPSSH), (char) => char.charCodeAt(0));
}

/**
 * Extracts the company id from widevine init PSSH data in the manifest and returns it
 * @param {Uint8Array} widevinePSSHArray - The PSSH init data for widevine key system
 * @returns {string} The company id
 */
function getCompanyIdFromWidevinePSSH(widevinePSSHArray) {
  try {
    const { contentId } = WidevinePsshData.decode(widevinePSSHArray.slice(32));
    const decodedContentId = atob(contentId);
    if (decodedContentId.indexOf(';') > 0) {
      return atob(contentId).split(';')[0];
    }
  } catch (error) {
    console.warn('Company id could not be extracted from widevine PSSH data. Default company id will be used', error);
  }

  return '';
}

/**
 * Returns the license url for widevine key system according to init PSSH data in the manifest
 * @param {string} dashManifest - The DASH Manifest of the asset
 * @returns {string} The license url
 */
function getISPWidevineLicenseUrl(dashManifest) {
  const widevinePSSHArray = getWidevinePSSHArrayFromDashManifest(dashManifest);
  if (widevinePSSHArray) {
    const companyId = getCompanyIdFromWidevinePSSH(widevinePSSHArray);
    if (companyId) {
      return `${ISP_BASE_WIDEVINE_LICENSE_URL}/${companyId}`;
    }
  }

  return '';
}

function getISPLicenseHeaders(request, { entitlementsToken }) {
  return {
    [ISP_LICENSE_HEADER_NAME]: entitlementsToken,
  };
}

export const ispPlayReadyConfig = {
  defaultLicenseUrl: ISP_PLAYREADY_LICENSE_URL,
  getLicenseHeaders: (...args) => ({
    ...getISPLicenseHeaders(...args),
    'Content-Type': 'text/xml; charset=utf-8',
  }),
};

export function getISPWidevineConfig(defaultCompanyId) {
  return {
    defaultLicenseUrl: `${ISP_BASE_WIDEVINE_LICENSE_URL}/${defaultCompanyId}`,
    getLicenseUrl: getISPWidevineLicenseUrl,
    getLicenseHeaders: getISPLicenseHeaders,
  };
}

export function getISPKeySystemsConfig(defaultCompanyId) {
  const keySystemsConfig = {
    playready: ispPlayReadyConfig,
    widevine: getISPWidevineConfig(defaultCompanyId),
  };

  return keySystemsConfig;
}

export function getBlimLicenseHeaders(consumerOrConsumerGetter) {
  return (request) => {
    let consumer = consumerOrConsumerGetter;
    if (typeof consumer === 'function') {
      consumer = consumer();
    }
    const oauth = OAuth({
      consumer,
      signature_method: 'HMAC-SHA1',
      hash_function: (baseString, key) => (
        HmacSHA1(baseString, key).toString(Base64)
      ),
    });

    const authorization = oauth.authorize(request);

    const additionalHeaders = oauth.toHeader(authorization);

    return additionalHeaders;
  };
}

export function getBlimPlayreadyConfig(host, consumer) {
  return {
    defaultLicenseUrl: `${host}/license/playready`,
    getLicenseHeaders: getBlimLicenseHeaders(consumer),
  };
}

export function getBlimWidevineConfig(host, consumer) {
  return {
    defaultLicenseUrl: `${host}/license/widevine`,
    getLicenseHeaders: getBlimLicenseHeaders(consumer),
  };
}

export function getBlimKeySystemsConfig(host, consumer) {
  return {
    widevine: getBlimWidevineConfig(host, consumer),
    playready: getBlimPlayreadyConfig(host, consumer),
  };
}

const urlsShape = PropTypes.shape({
  primary: PropTypes.string,
  backup: PropTypes.string,
});
export const urlsObjectShape = PropTypes.shape({
  dash: urlsShape,
  hls: urlsShape,
  smooth: urlsShape,
});

/**
 * Returns an array of sources to be used in the player as alternatives
 * @param {object} urlsObject - An orbis urls object
 * @param {string} entitlementsToken - The entitlements token for the source
 * @returns {object[]} An array with source objects
 */
export function getSourceObjectArray(urlsObject, entitlementsToken) {
  const {
    dash: { primary: primaryDashUrl, backup: backupDashUrl } = {},
    hls: { primary: primaryHlsUrl, backup: backupHlsUrl } = {},
    smooth: { primary: primarySmoothUrl, backup: backupSmoothUrl } = {},
  } = urlsObject;
  const urlsArray = [{
    dash: primaryDashUrl,
    hls: primaryHlsUrl,
    smooth: primarySmoothUrl,
    isPrimary: true,
  }, {
    dash: backupDashUrl,
    hls: backupHlsUrl,
    smooth: backupSmoothUrl,
    isPrimary: false,
  }];
  return urlsArray.filter((source) => source.dash || source.hls || source.smooth)
    .map((source) => ({
      ...source,
      entitlementsToken,
    }));
}
